// (c) 2023 Acellera Ltd http://www.acellera.com
// All Rights Reserved
// No redistribution in whole or part
//
/* eslint-disable no-restricted-globals */
import {
  FetchAppList,
  FetchDatasets,
  JobFilters,
  PriorityWorkerMessage,
  StartJobPolling,
  AppLimitations,
  FetchExamples,
  FetchProjects,
  Job,
  FetchQueueStatus,
} from "molkitstar-ui";
import { parseQueueJobInfo, parseUserJobInfo } from "./fetchJobsUtils";
import { executeWithTokenRefreshWorker } from "./executeWithTokenRefreshWorker";
import { FetchCathDataset } from "molkitstar-ui/PyodideTerminal/highPriorityWorkerTypes";

const postMessage_WRAPPER = (msg: PriorityWorkerMessage) =>
  self.postMessage(msg);

let filters: JobFilters = {
  newer_than: -1,
  older_than: -1,
  limit: 10,
  count: false,
};

let jobFetchProject: string = "default_project";

async function FetchAppExamples(msg: FetchExamples) {
  if (!msg.url || !msg.appId || !msg.filePath) {
    console.error("Bad message! Passed message is: ");
    console.log(msg);
    return;
  }
  try {
    const response = await executeWithTokenRefreshWorker(
      () =>
        fetch(`${msg.url}/v2/datacenter`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            startsWith: `apps/${msg.appId}/tests/file`,
          },
        }),
      msg.url
    );
    if (!response.success) {
      if (response.logout) postMessage_WRAPPER({ type: "logout" });
      postMessage_WRAPPER({
        type: "worker-error",
        error: "job fetch failed!",
      });
      return;
    }
    const examples = await response.result.json();
    if (examples.length === 0) {
      postMessage_WRAPPER({
        type: "worker-error",
        error: "No data found",
      });
      return;
    }
    const exampleID = examples[0].id;
    const fileName = msg.filePath.replace(/\/+$/, "").split("/").pop();
    const fileNameSplit = fileName?.split(".");
    const isDir = fileNameSplit && fileNameSplit.length <= 1;
    const ext = fileNameSplit
      ? fileNameSplit.length > 1
        ? fileNameSplit?.pop()
        : "zip"
      : "zip";
    const response2 = await executeWithTokenRefreshWorker(
      () =>
        fetch(`${msg.url}/v2/datacenter/${exampleID}`, {
          method: "GET",
          //@ts-ignore
          headers: {
            "Content-Type": "application/json",
            files: [msg.filePath, msg.filePath.replace(/^tests\//, "")],
            tar: isDir,
            type: "text/plain",
            extension: ext === "zip" ? "zip" : undefined,
          },
        }),
      msg.url
    );

    if (!response2.success) {
      if (response2.logout) postMessage_WRAPPER({ type: "logout" });
      postMessage_WRAPPER({
        type: "worker-error",
        error: "job fetch failed!",
      });
      return;
    }

    const isCompressed = ext && ["zip", "ckpt", "gz", "tar.gz"].includes(ext);
    const content = isCompressed
      ? await response2.result.arrayBuffer()
      : await response2.result.text();
    postMessage_WRAPPER({
      type: "example-file",
      fileContent: content,
      fileName: fileName ? fileName : "example",
    });

    return content;
  } catch (error) {
    postMessage_WRAPPER({
      type: "worker-error",
      error: `priority worker error: ${error}`,
    });
  }
}

self.onmessage = function handleMessageFromMain(e) {
  const msg: PriorityWorkerMessage = e.data;
  switch (msg.type) {
    case "start-job-polling":
      startJobPolling(msg);
      break;

    case "stop-job-polling":
      stopJobPolling();
      break;

    case "set-job-filters":
      setFilters(msg.filters);
      break;

    case "fetch-app-list":
      fetchPlaymoleculeApps(msg);
      break;

    case "fetch-datasets":
      fetchPlaymoleculeDatasets(msg);
      break;
    case "fetch-cath-dataset":
      fetchCathDatasets(msg);
      break;

    case "fetch-examples":
      FetchAppExamples(msg);
      break;
    case "fetch-projects":
      fetchProjects(msg);
      break;
    case "fetch-queue-status":
      fetchQueueStatus(msg);
      break;
    case "set-job-project":
      setJobsProject(msg.project);
      break;
    default:
      break;
  }
};

let THREAD_IS_BUSY = false;
let JOB_INTERVAL_ID: any;

const fetchPlaymoleculeJobs = async (msg: StartJobPolling) => {
  if (THREAD_IS_BUSY) return;

  if (!msg.url || !msg.userId) {
    console.error("url missing!");
    return;
  }

  THREAD_IS_BUSY = true;
  try {
    const response = await executeWithTokenRefreshWorker(
      () =>
        fetch(`${msg.url}/v2/jobs`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...filters,
            children: true,
            group: `${msg.userId}/${jobFetchProject}`,
          }),
        }),
      msg.url
    );

    THREAD_IS_BUSY = false;
    if (!response.success) {
      if (response.logout) postMessage_WRAPPER({ type: "logout" });
      postMessage_WRAPPER({
        type: "worker-error",
        error: "job fetch failed!",
      });
      return;
    }

    const jobs = await response.result.json();

    const parsedJobs: Job[] = [];

    for (let job of jobs) {
      parsedJobs.push(parseUserJobInfo(job));
    }

    const updatedList = assignChildrenJobs(parsedJobs);
    postMessage_WRAPPER({ type: "job-list", jobList: updatedList });
  } catch (error) {
    THREAD_IS_BUSY = false;
    postMessage_WRAPPER({
      type: "worker-error",
      error: `priority worker error: ${error}`,
    });
  }
};

const fetchPlaymoleculeApps = (msg: FetchAppList) => {
  /* No need to call THREAD_IS_BUSY here, it is a one-time operation. */

  if (!msg.url || !msg.appIds) {
    console.error("Bad message! Passed message is: ");
    console.log(msg);
    return;
  }

  let limits: AppLimitations | undefined;
  executeWithTokenRefreshWorker(
    () =>
      fetch(`${msg.url}/v2/limits`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
    msg.url
  )
    .then((x) => {
      if (x.success) {
        return x.result.text();
      }
      if (x.logout) postMessage_WRAPPER({ type: "logout" });
    })
    .then((text) => {
      try {
        if (text) {
          limits = JSON.parse(text); // Try to parse the response as JSON
        }
      } catch {}
    });
  executeWithTokenRefreshWorker(
    () =>
      fetch(`${msg.url}/v2/apps`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
    msg.url
  )
    .then((response) => {
      if (response.success) return response.result.json();
      if (response.logout) postMessage_WRAPPER({ type: "logout" });
      postMessage_WRAPPER({
        type: "worker-error",
        error: "job fetch failed!",
      });
    })
    .then((apps) => {
      const appIds = apps
        .filter((a: any) => msg.appIds.includes(a.name.toLowerCase()))
        .map((a: any) => a.id);

      const fetchManifest = (appname: string) =>
        executeWithTokenRefreshWorker(
          () =>
            fetch(`${msg.url}/v2/apps/${appname.toLowerCase()}/manifest`, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            }),
          msg.url
        );

      Promise.all(appIds.map(fetchManifest)).then((responseArr) => {
        Promise.all(
          responseArr.map((x) => {
            if (x.success) return x.result.json();
            else return undefined;
          })
        ).then((appManifests) => {
          postMessage_WRAPPER({
            type: "app-list",
            appList: JSON.stringify(
              appManifests.filter((e) => e !== undefined)
            ),
            limits: limits ? JSON.stringify(limits) : undefined,
          });
        });
      });
    })
    .catch((error) => {
      postMessage_WRAPPER({
        type: "worker-error",
        error: `priority worker error: ${error}`,
      });
    });
};
const fetchQueueStatus = async (msg: FetchQueueStatus) => {
  if (THREAD_IS_BUSY) return;
  if (!msg.url) {
    console.error("url missing!");
    return;
  }
  THREAD_IS_BUSY = true;
  try {
    const response = await executeWithTokenRefreshWorker(
      () =>
        fetch(`${msg.url}/v2/jobs`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            newer_than: -1,
            older_than: -1,
            limit: -1,
            not_in_status: [4, 5],
            count: false,
            children: false,
            all_users: true,
          }),
        }),
      msg.url
    );

    if (!response.success) {
      if (response.logout) postMessage_WRAPPER({ type: "logout" });
      postMessage_WRAPPER({
        type: "worker-error",
        error: "job fetch failed!",
      });
      THREAD_IS_BUSY = false;
      return;
    }
    const jobs = await response.result.json();
    const queueJobs = parseQueueJobInfo(jobs);

    THREAD_IS_BUSY = false;
    postMessage_WRAPPER({ type: "queue-status", queueJobs: queueJobs });
  } catch (error) {
    THREAD_IS_BUSY = false;
    postMessage_WRAPPER({
      type: "worker-error",
      error: `priority worker error: ${error}`,
    });
  }
};

const fetchProjects = async (msg: FetchProjects) => {
  if (!msg.url || !msg.userId) {
    console.error("Bad message! Passed message is: ");
    console.log(msg);
    return;
  }
  try {
    const response = await executeWithTokenRefreshWorker(
      () =>
        fetch(`${msg.url}/v2/datacenter`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            startsWith: msg.userId,
          },
        }),
      msg.url
    );
    if (!response.success) {
      if (response.logout) postMessage_WRAPPER({ type: "logout" });
      postMessage_WRAPPER({
        type: "worker-error",
        error: "get projects failed!",
      });
      return;
    }
    const datasets = await response.result.json();
    const projectsArray: string[] = datasets
      .map((x: any) => x.filepath?.split("/")[1])
      .filter((x: string | undefined) => x !== undefined);
    if (jobFetchProject) projectsArray.push(jobFetchProject);
    const projects = [...new Set(projectsArray)].sort();
    postMessage_WRAPPER({
      type: "projects",
      projects: projects,
    });
  } catch (error) {
    postMessage_WRAPPER({
      type: "worker-error",
      error: `priority worker error: ${error}`,
    });
  }
};

const fetchCathDatasets = (msg: FetchCathDataset) => {
  if (THREAD_IS_BUSY && !msg.priority) return;
  if (!msg.url) {
    console.error("Bad message! Passed message is: ");
    console.log(msg);
    return;
  }
  THREAD_IS_BUSY = true;

  const headers = {
    userOnly: "False",
    taggedOnly: "False",
    completedOnly: "False",
    datasetId: "",
    filePath: "",
    public: "True",
    startsWith: `CATH/`,
    tags: "cath",
    group: "",
    filelist: "True",
    execID: "",
  };

  executeWithTokenRefreshWorker(
    () =>
      fetch(`${msg.url}/v2/datacenter`, {
        method: "GET",
        headers: headers,
      }),
    msg.url
  )
    .then((response) => {
      if (!response.success) {
        if (response.logout) postMessage_WRAPPER({ type: "logout" });
        postMessage_WRAPPER({
          type: "worker-error",
          error: "job fetch failed!",
        });
        return;
      }
      return response.result.json();
    })
    .then((datasets) => {
      THREAD_IS_BUSY = false;
      postMessage_WRAPPER({
        type: "cath-dataset",
        datasets: datasets,
      });
    })
    .catch((error) => {
      THREAD_IS_BUSY = false;
      postMessage_WRAPPER({
        type: "worker-error",
        error: `priority worker error: ${error}`,
      });
    });
};

const fetchPlaymoleculeDatasets = (msg: FetchDatasets) => {
  if (THREAD_IS_BUSY && !msg.priority) return;
  if (!msg.url || !msg.project || !msg.userId) {
    console.error("Bad message! Passed message is: ");
    console.log(msg);
    return;
  }
  THREAD_IS_BUSY = true;

  const headers = {
    userOnly: "True",
    taggedOnly: "False",
    completedOnly: "False",
    datasetId: "",
    filePath: "",
    public: "",
    startsWith: `${msg.userId}/${msg.project}/`,
    tags: "",
    group: "",
    filelist: "True",
    execID: "",
  };

  executeWithTokenRefreshWorker(
    () =>
      fetch(`${msg.url}/v2/datacenter`, {
        method: "GET",
        headers: headers,
      }),
    msg.url
  )
    .then((response) => {
      if (!response.success) {
        if (response.logout) postMessage_WRAPPER({ type: "logout" });
        postMessage_WRAPPER({
          type: "worker-error",
          error: "job fetch failed!",
        });
        return;
      }
      return response.result.json();
    })
    .then((datasets) => {
      THREAD_IS_BUSY = false;
      postMessage_WRAPPER({
        type: "datasets",
        truncate: msg.userId,
        datasets: datasets,
        setCurrentDir: msg.setCurrentDir,
        project: msg.project,
      });
    })
    .catch((error) => {
      THREAD_IS_BUSY = false;
      postMessage_WRAPPER({
        type: "worker-error",
        error: `priority worker error: ${error}`,
      });
    });
};

const setFilters = (newFilters: JobFilters) => (filters = newFilters);

const setJobsProject = (newProject: string) => {
  jobFetchProject = newProject;
};

const startJobPolling = (msg: StartJobPolling) => {
  jobFetchProject = msg.project;
  JOB_INTERVAL_ID = setInterval(fetchPlaymoleculeJobs, 1000, msg);
};

const stopJobPolling = () => {
  if (JOB_INTERVAL_ID === undefined) return;
  for (let i = JOB_INTERVAL_ID; i > 0; i--) {
    clearInterval(i);
  }
};

const assignChildrenJobs = (parsedJobs: Job[]) => {
  parsedJobs.forEach((j) => {
    if (j.parentId !== "-") {
      const parentJob = parsedJobs.find((x) => x.traceId === j.parentId);

      if (!parentJob) {
        console.error(`parent job with id ${j.parentId} not found!`);
        return;
      }

      if (!parentJob.children) parentJob["children"] = [];
      parentJob.children.push(j);
    }
  });

  /* Remove children jobs from top level before returning */
  return parsedJobs.filter((j) => j.parentId === "-");
};
